import { Slot } from "@radix-ui/react-slot";
import { TooltipContentProps } from "@radix-ui/react-tooltip";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";

const buttonVariants = cva(
	// Disabled keeps each variant's own colors and just drops opacity, so a disabled button still
	// reads as the same control. Variants must not re-color themselves via `disabled:`.
	"inline-flex items-center gap-2 justify-center whitespace-nowrap text-sm font-medium ring-offset-background rounded-md transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 cursor-pointer max-h-full focus-visible:ring-2 focus-visible:ring-offset-2",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-stealth-button dark:hover:bg-stealth-button/90 dark:border dark:border-stealth-divider focus-visible:ring-black dark:focus-visible:bg-stealth-button",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive",
				outline:
					"border border-primary bg-transparent dark:border-white dark:hover:bg-white/5 dark:hover:text-white hover:bg-muted hover:text-muted-foreground dark:focus-visible:ring-white focus-visible:ring-primary/50",
				outlineDestructive:
					"border border-destructive bg-transparent text-destructive dark:border-white dark:focus-visible:ring-white focus-visible:ring-destructive",
				lightOutline:
					"border border-border bg-transparent dark:border-white dark:hover:bg-white/5 dark:hover:text-white hover:bg-accent hover:text-accent-foreground dark:focus-visible:ring-white focus-visible:ring-primary",
				secondary:
					"bg-secondary text-secondary-foreground dark:border dark:border-stealth-divider hover:bg-secondary/80 dark:focus-visible:ring-border focus-visible:ring-secondary",
				lightSecondary: "bg-secondary/15 text-secondary hover:bg-secondary/25",
				ghost: "hover:bg-muted hover:text-muted-foreground dark:hover:bg-stealth-gradient-blue dark:hover:text-foreground",
				ghostLight:
					"hover:bg-muted hover:text-muted-foreground dark:hover:bg-stealth-gradient-blue dark:hover:text-foreground text-gray-600",
				ghostStealth: "hover:bg-stealth-tooltip hover:text-white",
				ghostStealthOutline:
					"border border-white hover:bg-stealth-tooltip hover:text-white",
				ghostSecondary: "hover:bg-muted text-secondary",
				ghostDestructive: "text-destructive hover:bg-destructive/5",
				ghostStealthDestructive: "text-red-400 hover:bg-red-600 hover:text-white",
				ghostWarning: "text-orange-500 hover:bg-orange-500/5",
				link: "text-primary underline-offset-4 hover:opacity-90",
				linkSecondary: "text-secondary underline-offset-4 hover:opacity-90",
				linkWhite: "text-white underline-offset-4 hover:opacity-90",
				white: "bg-white text-primary hover:bg-white/90",
				black: "bg-black text-white hover:bg-black/90",
				stealthBlue: "bg-stealth-tooltip text-white hover:bg-stealth-blue/90",
				custom: "",
			},
			size: {
				default: "h-10 px-4 py-2",
				xs: "h-8 px-2",
				sm: "h-9 px-3",
				lg: "h-11 px-8",
				icon: "h-8 w-8 rounded-full",
				iconSmall: "h-6 w-6 rounded-full",
			},
			shape: {
				default: "",
				rounded: "rounded-full",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "sm",
			shape: "default",
		},
	},
);

export interface ButtonVariantProps extends VariantProps<typeof buttonVariants> {}
export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		ButtonVariantProps {
	asChild?: boolean;
	tooltip?: string;
	tooltipProps?: TooltipContentProps;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{ className, variant, size, shape, asChild = false, tooltip, tooltipProps = {}, ...props },
		ref,
	) => {
		const Comp = asChild ? Slot : "button";
		const buttonComponent = (
			<Comp
				className={cn(buttonVariants({ variant, size, shape, className }))}
				ref={ref}
				{...props}
			/>
		);

		if (tooltip) {
			// A disabled button emits no pointer events, so it can never trigger its own tooltip.
			// Wrap it so the span receives the hover instead — otherwise the explanation for why
			// the button is disabled is exactly the one a user can't see.
			const trigger = props.disabled ? (
				<span className="inline-flex [&>button]:pointer-events-none">
					{buttonComponent}
				</span>
			) : (
				buttonComponent
			);

			return (
				<TooltipProvider>
					<Tooltip delayDuration={100}>
						<TooltipTrigger asChild>{trigger}</TooltipTrigger>
						<TooltipContent {...tooltipProps}>
							<p>{tooltip}</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			);
		}

		return buttonComponent;
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
